import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { fmtDateGlobal } from '../lib/dateFormat';

const TYPE_CONFIG = {
  SO: { label: 'Sales Orders', color: 'bg-blue-100 text-blue-800', cardBg: 'from-blue-500 to-blue-600' },
  SE: { label: 'Sales Entries', color: 'bg-emerald-100 text-emerald-800', cardBg: 'from-emerald-500 to-emerald-600' },
  SR: { label: 'Sales Returns', color: 'bg-amber-100 text-amber-800', cardBg: 'from-amber-500 to-amber-600' },
  PO: { label: 'Purchase Orders', color: 'bg-indigo-100 text-indigo-800', cardBg: 'from-indigo-500 to-indigo-600' },
  PE: { label: 'Purchase Entries', color: 'bg-teal-100 text-teal-800', cardBg: 'from-teal-500 to-teal-600' },
  PR: { label: 'Purchase Returns', color: 'bg-rose-100 text-rose-800', cardBg: 'from-rose-500 to-rose-600' },
};

const DailySummary = ({ user }) => {
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/daily-summary?date=${date}`);
      setData(res.data);
      if (activeFilters.size === 0 && res.data?.allowed_types?.length) {
        setActiveFilters(new Set(res.data.allowed_types));
      }
    } catch {
      toast.error('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleFilter = (type) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const filteredTransactions = data?.transactions?.filter(t => activeFilters.has(t.type)) || [];

  const totalAmount = filteredTransactions.reduce((s, t) => {
    if (t.type === 'SR' || t.type === 'PR') return s - t.amount;
    return s + t.amount;
  }, 0);
  const totalQty = filteredTransactions.reduce((s, t) => s + (t.qty || 0), 0);

  const changeDate = (offset) => {
    const d = new Date(date);
    d.setDate(d.getDate() + offset);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  return (
    <div className="space-y-4 p-6" data-testid="daily-summary-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="daily-summary-title">
            Daily Summary
          </h1>
          <p className="text-sm text-gray-500">Track daily activity across all transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => changeDate(-1)} data-testid="prev-date-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
          </Button>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-40 h-8 text-sm" data-testid="date-picker" />
          <Button variant="outline" size="sm" onClick={() => changeDate(1)} data-testid="next-date-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
          </Button>
          <Button variant="outline" size="sm" className="text-xs" data-testid="today-btn"
            onClick={() => {
              const d = new Date();
              setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
            }}>Today</Button>
        </div>
      </div>

      {/* Summary Cards */}
      {data && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-cards">
          {(data.allowed_types || []).map(type => {
            const s = data.summary[type] || { count: 0, amount: 0, qty: 0 };
            const cfg = TYPE_CONFIG[type];
            return (
              <Card key={type} className={`cursor-pointer border-2 transition-all ${activeFilters.has(type) ? 'border-gray-400 shadow-md' : 'border-transparent opacity-60'}`}
                onClick={() => toggleFilter(type)} data-testid={`summary-card-${type}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={`${cfg.color} text-[10px]`}>{type}</Badge>
                    <span className="text-lg font-bold text-gray-900">{s.count}</span>
                  </div>
                  <p className="text-[11px] text-gray-500 font-medium">{cfg.label}</p>
                  <div className="mt-1.5 space-y-0.5">
                    <p className="text-xs font-semibold text-gray-800">{s.amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
                    <p className="text-[10px] text-gray-400">{s.qty} pcs</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filter Chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 font-medium">Filter:</span>
        {(data?.allowed_types || []).map(type => (
          <button key={type} onClick={() => toggleFilter(type)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${activeFilters.has(type)
              ? `${TYPE_CONFIG[type].color} ring-1 ring-gray-300`
              : 'bg-gray-100 text-gray-400'}`}
            data-testid={`filter-chip-${type}`}>
            {type}
          </button>
        ))}
        {activeFilters.size > 0 && activeFilters.size < (data?.allowed_types?.length || 0) && (
          <button onClick={() => setActiveFilters(new Set(data?.allowed_types || []))} className="text-xs text-blue-600 hover:underline ml-1" data-testid="show-all-btn">Show All</button>
        )}
      </div>

      {/* Net Totals Bar */}
      {data && !loading && filteredTransactions.length > 0 && (
        <Card className="bg-gray-900 text-white" data-testid="net-totals-bar">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-xs font-medium opacity-70">Showing {filteredTransactions.length} transactions</span>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-[10px] opacity-60">Net Amount</p>
                <p className="text-sm font-bold">{totalAmount.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] opacity-60">Total Qty</p>
                <p className="text-sm font-bold">{totalQty.toLocaleString('en-IN')} pcs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-gray-400">Loading...</CardContent></Card>
      ) : filteredTransactions.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400" data-testid="no-transactions">
          No transactions found for {fmtDateGlobal(date)}
        </CardContent></Card>
      ) : (
        <Card data-testid="transactions-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Type</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Number</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Party</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">City</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Qty</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Amount</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((t, i) => (
                    <tr key={`${t.type}-${t.number}-${i}`} className="border-b last:border-0 hover:bg-gray-50 transition-colors" data-testid={`txn-row-${i}`}>
                      <td className="px-4 py-2.5">
                        <Badge className={`${TYPE_CONFIG[t.type]?.color} text-[10px]`}>{t.type}</Badge>
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{t.number}</td>
                      <td className="px-4 py-2.5 text-gray-700 font-medium">{t.party}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{t.city}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-700">{t.qty}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-900">
                        {t.amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          t.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          t.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' :
                          t.status === 'packed' ? 'bg-blue-100 text-blue-700' :
                          t.status === 'received' ? 'bg-teal-100 text-teal-700' :
                          t.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{t.status?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{t.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DailySummary;
